<template>
  <section class="ws-advanced-search">
    <ws-filter-item
      id="k"
      :type="filterType.SEARCH"
      :label="$t('Наименование ОО')"
      @ready="onFilterReady"
      @change="onFilter">
    </ws-filter-item>
    <header class="ws-advanced-search__header" @click="isOpen = !isOpen">
      <h3 class="ws-advanced-search__title has-text-primary is-size-2" v-text="title"></h3>
      <div class="ws-advanced-search__actions">
        <button class="ws-button button is-text"
                :aria-controls="id">
          <ws-icon
            class="ws-button__icon"
            name="arrow-thin"
            color="#000000"
            :class="{'ws-icon--left': !isOpen}"
            :fill="false"
            :title="$t('Свернуть/Развернуть')"
            width="17"
            height="11"
          />
        </button>
      </div>
    </header>
    <b-collapse
      :aria-id="id"
      class="ws-advanced-search__form"
      :open.sync="isOpen">
      <div class="columns is-multiline">
        <div class="column is-12">
          <b-field class="ws-field"
                   :label="$t('Адрес')">
            <b-autocomplete
                    class="ws-field__control"
                    name="address"
                    field="value"
                    @select="selectAddress"
                    @keyup.native="getAddressList($event); onAddressInput()"
                    @focus="getAddressList"
                    v-model="address.value"
                    :loading="isAddressPending"
                    :data="address.list">
              <template slot="empty" v-text="$t('Ничего не найдено')"></template>
            </b-autocomplete>
          </b-field>
        </div>
        <div class="column">
          <ws-permission-node :resources="['resources/region']">
            <ws-filter-item
              id="region_guid"
              field="title.display"
              :type="filterType.REFBOOK"
              :label="$t('Регион')"
              :value="region_guid.guid"
              entity="public/refbook/region"
              :params="{ country: config.default_country_code }"
              @ready="onFilterReady($event); onRegionFilterReady($event)"
              @change="onFilter($event); onRegionFilter($event)">
            </ws-filter-item>
          </ws-permission-node>
        </div>
        <div class="column">
          <ws-filter-item
            id="locality_guid"
            field="title.display"
            :type="filterType.REFBOOK"
            :label="$t('Населенный пункт')"
            :disabled="!region_guid"
            entity="public/refbook/locality"
            :value="locality_guid.guid"
            :params="{ country: config.default_country_code, region_guid }"
            @ready="onFilterReady"
            @change="onFilter">
          </ws-filter-item>
        </div>
      </div>
    </b-collapse>
  </section>
</template>

<script>
import { mapActions } from 'vuex'
import { constants } from '@/services/constants.service'
import { debounce } from 'debounce'
import filterMixin from '@/mixins/filter.mixin'
import WsFilterItem from '@/components/common/WsFilterItem'
import filterType from '@/components/common/data/filter-type.enum'

export default {
  name: 'WsOrgsFilter',
  components: { WsFilterItem },
  mixins: [ filterMixin ],
  data () {
    return {
      constants,
      config: __config,
      filterType,
      isAddressPending: false,
      locality_guid: {
        value: '',
        guid: null
      },
      address: {
        value: '',
        guid: null,
        list: [],
        isDisabled: false
      },
      region_guid: {
        value: '',
        guid: null
      },
      dontChangeAddress: false,
      isAddressSelected: false
    }
  },
  methods: {
    onRegionFilter (filterChangeDto) {
      if (filterChangeDto.component.id === 'region_guid') {
        this.region_guid = {
          value: filterChangeDto.value
        }
      }
    },
    onRegionFilterReady (filterChangeDto) {
      if (filterChangeDto.component.id === 'region_guid') {
        this.region_guid = {
          value: filterChangeDto.value
        }
      }
    },
    getFormattedAddress (data) {
      const address = []

      if (data.settlement_type === 'мкр') {
        address.push(data.settlement_with_type)
      }

      if (data.street_with_type) {
        address.push(data.street_with_type)
      }

      let addr = ''

      if (data.house_type) {
        addr += `${data.house_type} ${data.house}`
      }

      if (data.block_type) {
        addr += ` ${data.block_type} ${data.block}`
      }

      if (data.flat_type) {
        addr += ` ${data.flat_type} ${data.flat}`
      }

      address.push(addr)

      return address.join(', ').trim()
    },
    async selectAddress (address) {
      if (address) {
        this.dontChangeAddress = true

        const details = address.data
        this.locality_guid.value = details.city_with_type || details.settlement
        this.region_guid.value = details.region

        let region = null
        let locality = null

        if (this.region_guid.value) {
          const [ currentRegion ] = await this.findRegion({
            params: { country: __config.default_country_code },
            query: { k: this.region_guid.value }
          })
          region = currentRegion
        }

        if (this.locality_guid.value) {
          const [currentLocality] = await this.findLocality({
            params: { country: __config.default_country_code },
            query: { k: this.locality_guid.value }
          })
          locality = currentLocality
        }

        this.region_guid.value = region ? region.title.display : ''
        this.region_guid.guid = region ? region.guid : ''
        this.locality_guid.guid = locality ? locality.guid : ''

        this.newFilter.region_guid = region && region.guid ? region.guid : ''
        this.newFilter.locality_guid = locality && locality.guid ? locality.guid : ''

        this.address.value = this.getFormattedAddress(details)
        await this.$router.push({ query: { ...this.$route.query, address_q: this.address.value } }).catch(_ => {})

        this.newFilter.address_q = this.address.value

        this.$emit('change', this.newFilter)
        this.isAddressSelected = true
      } else {
        if (!this.dontChangeAddress) {
          this.address.value = null
          await this.$router.push({ query: { ...this.$route.query, address_q: this.address.value } }).catch(_ => {})

          this.newFilter.address_q = this.address.value

          this.$emit('change', this.newFilter)
        } else {
          this.dontChangeAddress = false
        }
      }
    },
    getAddressList: debounce(async function () {
      const query = this.address.value
      this.isAddressPending = true
      const list = await this.getAddress({
        query: query
      })
      this.isAddressPending = false
      this.address.list = list
    }, 200),

    onAddressInput () {
      if (this.isAddressSelected) {
        this.address.value = null
        this.$router.push({ query: { ...this.$route.query, address_q: this.address.value } }).catch(_ => {})

        this.newFilter.address_q = this.address.value

        this.$emit('change', this.newFilter)
        this.isAddressSelected = false
      }
    },

    ...mapActions('public/refbook/region', {
      findRegion: 'find',
      getRegion: 'getRegion',
      getAddress: 'getAddress'
    }),
    ...mapActions('public/refbook/locality', {
      findLocality: 'find'
    })
  },
  beforeMount () {
    this.address.value = this.$route.query && this.$route.query.address_q
    if (this.address.value) {
      this.newFilter.address_q = this.address.value
      this.isAddressSelected = true
    }
  }
}
</script>
